// Bonus guide — the comp plan, opened from the Bonus Projection tile.
//
// Casey, 2026-08-20: "an icon that if clicked unfolds that runbook with the
// comp details, that way it's hyper transparent." A rep should be able to see
// their number and, in one click, read exactly how it was arrived at without
// leaving the dashboard or being told to go find a document.
//
// The content is NOT duplicated here. It is the same squish-docs file that
// syncs into Doc Dash (runbooks/rep-bonus.md), fetched by its External_Id__c.
// A second copy of the comp plan living in JSX is a copy that would drift from
// the one people are told is authoritative, and comp is the last thing that
// should have two versions.
//
// There is no fetch-one-doc-by-external-id endpoint, so this pulls the
// runbooks category and picks its doc out. That is one request, made only when
// the panel is opened, and it keeps this to a frontend change — worth more
// than the smaller payload right now.

import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { fetchDocs } from '../api';

// The doc's identity in Salesforce is its path in squish-docs. If the file is
// ever renamed, this is the one place that has to move with it.
const GUIDE_EXTERNAL_ID = 'runbooks/rep-bonus.md';
const GUIDE_CATEGORY = 'runbooks';

// The panel header already carries the title, so the file's own leading H1
// would print it twice. Only the FIRST heading is dropped, and only if the doc
// opens with one — every section heading inside survives.
function withoutLeadingH1(markdown) {
  const lines = String(markdown || '').split('\n');
  if (!/^\s*#\s+\S/.test(lines[0] || '')) return String(markdown || '');
  let i = 1;
  while (i < lines.length && lines[i].trim() === '') i += 1;
  return lines.slice(i).join('\n');
}

export default function BonusGuide({ onClose }) {
  const [doc, setDoc]         = useState(null);
  const [state, setState]     = useState('loading');   // loading | ready | missing | error
  const [error, setError]     = useState('');

  useEffect(() => {
    let alive = true;
    fetchDocs({ category: GUIDE_CATEGORY })
      .then(data => {
        if (!alive) return;
        const found = (data.docs || []).find(d => d.External_Id__c === GUIDE_EXTERNAL_ID);
        if (found) { setDoc(found); setState('ready'); }
        else setState('missing');
      })
      .catch(e => { if (alive) { setError(e.message); setState('error'); } });
    return () => { alive = false; };
  }, []);

  // Escape closes, matching every other panel in the CRM.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, backdropFilter: 'blur(2px)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="How the bonus is calculated"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(860px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 80px)',
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
          zIndex: 1001, boxShadow: '0 18px 56px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '16px 22px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              {doc?.Title__c || 'How your bonus is calculated'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 3 }}>
              {/* Naming the source is part of the transparency: this is the same
                  document in Doc Dash, not a summary written for this panel. */}
              squish-docs · {GUIDE_EXTERNAL_ID}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
              fontSize: 18, lineHeight: 1, padding: 4, flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '20px 22px 28px', overflowY: 'auto' }}>
          {state === 'loading' && (
            <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>
          )}

          {state === 'error' && (
            <div style={{ fontSize: 13, color: 'var(--accent2)', fontFamily: 'var(--mono)', lineHeight: 1.7 }}>
              Couldn't load the guide — {error}
            </div>
          )}

          {/* Not an error: the doc syncs from git, so before that first sync it
              genuinely is not there yet. Say which, and where it comes from,
              rather than showing an empty panel. */}
          {state === 'missing' && (
            <div style={{ fontSize: 13, color: 'var(--badge-yellow)', fontFamily: 'var(--mono)', lineHeight: 1.7 }}>
              The bonus guide hasn't synced into Doc Dash yet.
              <div style={{ color: 'var(--text3)', marginTop: 6 }}>
                It arrives from squish-docs (<code>{GUIDE_EXTERNAL_ID}</code>) on the next push to main.
              </div>
            </div>
          )}

          {state === 'ready' && (
            <div
              className="doc-markdown"
              style={{ fontFamily: 'var(--font)', fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}
            >
              <ReactMarkdown skipHtml remarkPlugins={[remarkGfm]}>
                {withoutLeadingH1(doc.Content__c) || '*No content*'}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
